import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useUserReservations } from "@/hooks/reservations/useUserReservations";
import { useUploadReservationReceipt } from "@/hooks/reservations/useUploadReservationReceipt";
import { ArrowLeft, Calendar, Clock, MapPin, Upload, CalendarCheck, Eye } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useState, useRef } from "react";
import { ReservationReceiptModal } from "./ReservationReceiptModal";

// Helper function to parse date strings locally without timezone issues
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

interface UserReservationsProps {
  onBack: () => void;
}

export const UserReservations = ({ onBack }: UserReservationsProps) => {
  const { data: reservations = [], isLoading } = useUserReservations();
  const uploadReceipt = useUploadReservationReceipt();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedReservationId, setSelectedReservationId] = useState<string | null>(null);
  const [receiptModal, setReceiptModal] = useState<{
    isOpen: boolean;
    receiptUrl: string | null;
    title: string;
  }>({
    isOpen: false,
    receiptUrl: null,
    title: ''
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold">Cargando...</h2>
        </div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-1/3"></div>
              </CardHeader>
              <CardContent>
                <div className="h-16 bg-muted rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500/60 text-white ml-4';
      case 'pending': return 'bg-warning text-warning-foreground ml-4';
      case 'cancelled': return 'bg-destructive text-destructive-foreground ml-4';
      default: return 'bg-muted text-muted-foreground ml-4';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Confirmada';
      case 'pending': return 'Pendiente';
      case 'cancelled': return 'Cancelada';
      default: return status;
    }
  };

  const handleFileSelect = (reservationId: string) => {
    setSelectedReservationId(reservationId);
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && selectedReservationId) {
      uploadReceipt.mutate({
        reservationId: selectedReservationId,
        file
      });
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setSelectedReservationId(null);
  };

  const handleViewReceipt = (receiptUrl: string, title: string) => {
    setReceiptModal({
      isOpen: true,
      receiptUrl,
      title
    });
  };

  return (
    <div className="space-y-4 mt-1">
      <div className="flex items-center gap-2">
        <CalendarCheck className="h-6 w-6" />
        <h2 className="text-2xl font-semibold">Mis reservas</h2>
      </div>

      {reservations.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            No tienes reservas realizadas aún.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reservations.map((reservation) => (
            <Card key={reservation.id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{reservation.reservation_items?.name}</span>
                  <Badge className={getStatusColor(reservation.status)}>
                    {getStatusText(reservation.status)}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1  md:grid-cols-3 gap-4">
                  {reservation.reservation_items?.image_url && (
                    <div className="aspect-video rounded-lg overflow-hidden">
                      <img 
                        src={reservation.reservation_items.image_url} 
                        alt={reservation.reservation_items.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  <div className="space-y-2 md:col-span-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>{reservation.reservation_items?.reservation_categories?.name}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {format(parseLocalDate(reservation.reservation_date), 'PPP', { locale: es })}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4" />
                      <span>{reservation.start_time} - {reservation.end_time}</span>
                    </div>
                    
                    {reservation.gym_message && (
                      <div className="bg-muted/50 p-2 rounded-md border border-muted">
                        <p className="text-sm text-muted-foreground">
                          {reservation.gym_message}
                        </p>
                      </div>
                    )}
                    
                    <div className="pt-2 flex items-center justify-between">
                      <span className="font-medium">
                        Total: ${reservation.total_price}
                      </span>
                      
                      {/* Receipt upload/view section for confirmed reservations */}
                      {reservation.status === 'confirmed' && (
                        <div className="flex gap-2">
                          {reservation.payment_receipt_url ? (
                            <div className="flex gap-2 flex-col sm:flex-row">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewReceipt(
                                  reservation.payment_receipt_url!,
                                  reservation.reservation_items?.name || 'Reserva'
                                )}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                Ver comprobante
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleFileSelect(reservation.id)}
                                disabled={uploadReceipt.isPending}
                              >
                                <Upload className="h-4 w-4 mr-2" />
                                Cambiar
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleFileSelect(reservation.id)}
                              disabled={uploadReceipt.isPending}
                            >
                              {uploadReceipt.isPending && selectedReservationId === reservation.id ? (
                                'Subiendo...'
                              ) : (
                                <>
                                  <Upload className="h-4 w-4 mr-2" />
                                  Subir comprobante
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      
      {/* Receipt viewing modal */}
      <ReservationReceiptModal
        isOpen={receiptModal.isOpen}
        onClose={() => setReceiptModal({ isOpen: false, receiptUrl: null, title: '' })}
        receiptUrl={receiptModal.receiptUrl}
        reservationTitle={receiptModal.title}
      />
    </div>
  );
};